import { YhcEstimateFormFrontService } from './YhcEstimateFormFrontService'
import {
  ModelKazaiKaguidoOptionDestinationQuestionAndAnswer,
  ModelKazaiOptionServicesOneSelection,
  ModelYhcKazaiTypesFullRelation,
} from '../../fireModels/yhcDataTypes'
import { reactive } from 'vue'

/**
 * 家財ごとの選択されたオプションを保持
 */
export type YhcEFInputSelectedOptionsByServiceId = {
  [serviceId: number]: ModelKazaiOptionServicesOneSelection[]
}

/**
 * 家財ごとに選択した移動先質問と回答を保持
 */
export type YhcEFInputSelectedDestinationQuestionAndOptionId = {
  [questionId: number]: ModelKazaiKaguidoOptionDestinationQuestionAndAnswer
}

/**
 * 家財おまかせ便の家具家電移動オプションにてユーザが追加した家財のデータ
 * 主に `YhcEFKazaiKaguidoDetailInput.vue` で使用, 生成され YhcEFUserInputDataHandler にて保持される
 * 家財単体での料金計算などを行う
 *
 * ## 責任範囲
 * - 選択された家財種別のIDを保持する
 * - 選択されたオプションを保持する
 * - 選択された移動先質問を保持する
 * - 数量を保持する(2024.10現在は 1 固定)
 * - 自身のオプション料金を算出する
 * - 自身の移動先料金を算出する
 * - 自身の小計金額を算出する
 *
 * ## 責任範囲外
 * - 他の家財の情報
 * - 郵便番号や都道府県などの配送先情報
 */
export class YhcEFInputOneKazaiTypeDataObjectForKaguidoOption {
  public id: string // 追加時に random 生成, unique を振る
  // 家財のID
  public kazaiTypeId: number = null
  public selectedOptionsByServiceId: YhcEFInputSelectedOptionsByServiceId = {} // この家財に選択されたオプション
  public selectedDestinationQuestionAndAnswerByQuestionId: YhcEFInputSelectedDestinationQuestionAndOptionId =
    {} // この家財に選択された移動先質問
  public readonly quantity: number = 1 // 一旦固定で... 後々 必要になるかもしれない
  public readonly $efs: YhcEstimateFormFrontService

  /**
   * "その他" の場合に ユーザが入力する家財種別の名前
   */
  public userInputName: string = ''
  constructor(
    $efs: YhcEstimateFormFrontService, // 親サービスへの参照 (循環参照になる)
    initData: Partial<YhcEFInputOneKazaiTypeDataObjectForKaguidoOption> = null,
  ) {
    // JSON に変換するときに循環参照 Error になるので、 non-enumerable にする
    Object.defineProperty(this, '$efs', {
      value: $efs,
      enumerable: false,
    })
    if (initData) {
      Object.assign(this, initData)
    }
    // ID がなければ生成
    if (!initData?.id) {
      // @ts-ignore
      this.id = $core.$utils.generateUUIDV4()
    }
  }

  get isAddable(): boolean {
    if (!this.kazaiTypeId) {
      return false
    }
    if (this.isUserInputNameRequired && !this.userInputName) {
      return false
    }
    if (!this.isAllDestinationQuestionAnswered) {
      return false
    }
    if (this.hasError) {
      return false
    }
    return true
  }

  get kazaiType(): ModelYhcKazaiTypesFullRelation | null {
    return this.$efs.allKazaiTypesById[this.kazaiTypeId]
  }

  get availableServiceOptionIdsBasedOnKazaiType(): number[] {
    return this.kazaiType.serviceOptions.map((option) => option.service.id)
  }

  /**
   * kazaiType が 保持する 正しいオプションのみ を返す
   */
  get availableSelectedOptionsByServiceId(): {
    [serviceId: number]: ModelKazaiOptionServicesOneSelection[]
  } {
    if (!this.kazaiType) {
      return {}
    }
    const availableServiceOptionIdsBasedOnKazaiType =
      this.availableServiceOptionIdsBasedOnKazaiType
    return Object.keys(this.selectedOptionsByServiceId).reduce((acc, serviceId) => {
      if (availableServiceOptionIdsBasedOnKazaiType.includes(Number(serviceId))) {
        acc[serviceId] = this.selectedOptionsByServiceId[serviceId]
      }
      return acc
    }, {})
  }
  get optionTotalPrice(): number {
    return Object.keys(this.availableSelectedOptionsByServiceId).reduce(
      (acc, serviceId) => {
        const selectedOptions = this.selectedOptionsByServiceId[serviceId]
        return (
          acc +
          selectedOptions.reduce((acc, selectedOption) => {
            return acc + selectedOption.price
          }, 0)
        )
      },
      0,
    )
  }
  // 移動先選択に対する合計金額
  get destinationTotalPrice(): number {
    return Object.keys(this.selectedDestinationQuestionAndAnswerByQuestionId).reduce(
      (acc, questionId) => {
        const selectedAnswer =
          this.selectedDestinationQuestionAndAnswerByQuestionId[questionId]
        return acc + selectedAnswer.additionalPrice
      },
      0,
    )
  }
  get totalPrice(): number {
    return (this.optionTotalPrice + this.destinationTotalPrice) * this.quantity
  }

  /**
   * 編集用などで clone するためのメソッド
   */
  clone(): YhcEFInputOneKazaiTypeDataObjectForKaguidoOption {
    const cloned = reactive(
      new YhcEFInputOneKazaiTypeDataObjectForKaguidoOption(this.$efs),
    )
    cloned.kazaiTypeId = this.kazaiTypeId
    cloned.userInputName = this.userInputName
    cloned.selectedOptionsByServiceId = JSON.parse(
      JSON.stringify(this.selectedOptionsByServiceId),
    )
    cloned.selectedDestinationQuestionAndAnswerByQuestionId = JSON.parse(
      JSON.stringify(this.selectedDestinationQuestionAndAnswerByQuestionId),
    )
    return cloned as YhcEFInputOneKazaiTypeDataObjectForKaguidoOption
  }

  /**
   * ユーザが家財の名称を 入力することが必須であるかどうか
   */
  get isUserInputNameRequired(): boolean {
    return this.kazaiType.displayNameFormEnabled
  }

  /**
   * 移動先質問の回答が全て選択されているかどうか
   */
  get isAllDestinationQuestionAnswered(): boolean {
    // selectedDestinationQuestionAndAnswerByQuestionId のquestionIdごとにoptionTextが一つでも未設定だったらfalseを返す
    return Object.keys(this.selectedDestinationQuestionAndAnswerByQuestionId).every(
      (questionId) => {
        return !!this.selectedDestinationQuestionAndAnswerByQuestionId[questionId]
          .optionId
      },
    )
  }

  /**
   * エラーがあるかどうか (サイズエラー or 配送エリアエラー)
   */
  get hasError(): boolean {
    // Notice: this.$efs.$userInputData.hasError を使うと infinite loop になるので注意
    if (this.$efs.$userInputData.hasPickupAndDeliveryError) {
      return true
    }
    return false
  }
}
